// The inbound address (INBOUND_ADDRESS on the server, log@in.deltapartners-me.com by default): one setting, shown
// wherever the app tells people where to forward or copy email. Nothing here hard-codes it. Also the picker that
// places an email the assistant could not place (Log it).
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProposedAction } from '@dpw/shared';
import { api } from '../api';
import { useEmailStatus, useSpaces } from '../lib/queries';
import { useCardChange } from '../lib/decide';
import { ErrorNote } from './bits';

/** The address, once the server has said it (null until then). */
export function useInboundAddress(): string | null {
  return useEmailStatus().data?.inboundAddress ?? null;
}

/** "Forward any email to <address>" help: renders nothing until the address is known, so no wrong address flashes. */
export function ForwardHelp({ children }: { children: (address: ReactNode, connected: boolean) => ReactNode }) {
  const s = useEmailStatus().data;
  if (!s) return null;
  return <>{children(<span className="mono inaddr">{s.inboundAddress}</span>, s.receiving)}</>;
}

/** Every track the person can see, with its space, for "Space and track" pickers. */
export function useAllTracks() {
  const spaces = useSpaces();
  return useQuery({
    queryKey: ['all-tracks', (spaces.data ?? []).map(s => s.id)], enabled: !!spaces.data,
    queryFn: async () => (await Promise.all((spaces.data ?? []).map(s => api.spaces.get(s.id)))).flatMap(d => d.tracks.map(t => ({ id: t.id, name: t.name, space: d.name }))),
    staleTime: 60_000,
  });
}

/**
 * Log it, on an email the assistant could not place: pick the space and track by hand. The card becomes a new
 * activity there (its next version, "Updated"), where "Log as" can make it an update on an open activity instead;
 * then Approve and log, as with any forwarded email.
 */
export function PlacePicker({ a, phone }: { a: ProposedAction; phone?: boolean }) {
  const tracks = useAllTracks();
  const qc = useQueryClient();
  const id = useId();
  const place = useCardChange((trackId: string) => api.actions.retarget(a.id, { trackId, activityId: null }));
  // Focus the picker once it can be used (it is disabled until the tracks arrive, and a disabled control takes no focus).
  const sel = useRef<HTMLSelectElement>(null);
  const ready = !!tracks.data;
  useEffect(() => { if (ready && !phone) sel.current?.focus(); }, [ready, phone]);
  const groups = new Map<string, { id: string; name: string }[]>();
  for (const t of tracks.data ?? []) groups.set(t.space, [...(groups.get(t.space) ?? []), t]);
  return (
    <div className="place" data-place>
      <label className="fld" htmlFor={id}>Where it goes
        <select id={id} ref={sel} className="inp" value="" disabled={place.isPending || !tracks.data}
          onChange={e => { if (e.target.value) place.mutate(e.target.value, { onSuccess: () => void qc.invalidateQueries({ queryKey: ['email-status'] }) }); }}>
          <option value="">Pick a space and track</option>
          {[...groups.entries()].map(([space, ts]) => <optgroup key={space} label={space}>{ts.map(t => <option key={t.id} value={t.id}>{space} · {t.name}</option>)}</optgroup>)}
        </select>
      </label>
      <div className="xs mut">It becomes a new activity there. You can make it an update on an activity instead, then approve.</div>
      {place.isPending && <span className="spin" />}
      {place.error ? <ErrorNote error={place.error} /> : null}
    </div>
  );
}
